import { createTheme, alpha } from '@mui/material/styles';

export const DEFAULT_THEME_CONFIG = {
  mode: 'light',
  primaryColor: '#667eea',
  secondaryColor: '#764ba2',
};

export const normalizeThemeConfig = (settings = {}) => ({
  mode: settings.theme_mode === 'dark' ? 'dark' : 'light',
  primaryColor: settings.primary_color || DEFAULT_THEME_CONFIG.primaryColor,
  secondaryColor: settings.secondary_color || DEFAULT_THEME_CONFIG.secondaryColor,
});

export const themeConfigToSettings = (config) => ({
  theme_mode: config.mode === 'dark' ? 'dark' : 'light',
  primary_color: config.primaryColor,
  secondary_color: config.secondaryColor,
});

export const buildAppTheme = (config = DEFAULT_THEME_CONFIG) => {
  const mode = config.mode === 'dark' ? 'dark' : 'light';
  const primaryMain = config.primaryColor || DEFAULT_THEME_CONFIG.primaryColor;
  const secondaryMain = config.secondaryColor || DEFAULT_THEME_CONFIG.secondaryColor;

  return createTheme({
    palette: {
      mode,
      primary: { main: primaryMain },
      secondary: { main: secondaryMain },
      ...(mode === 'dark'
        ? {
            background: {
              default: '#121212',
              paper: '#1e1e1e',
            },
          }
        : {
            background: {
              default: '#eef2f6',
              paper: '#ffffff',
            },
            divider: '#e2e8f0',
            text: {
              primary: '#1e293b',
              secondary: '#64748b',
            },
          }),
    },
    typography: {
      fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
      h4: { fontWeight: 600, letterSpacing: '-0.02em' },
      h5: { fontWeight: 600 },
      h6: { fontWeight: 600 },
      subtitle2: { fontWeight: 600 },
    },
    shape: {
      borderRadius: 10,
    },
    shadows:
      mode === 'light'
        ? [
            'none',
            '0 1px 2px rgba(15, 23, 42, 0.05)',
            '0 4px 12px rgba(15, 23, 42, 0.06)',
            '0 8px 24px rgba(15, 23, 42, 0.08)',
            ...Array(21).fill('0 8px 24px rgba(15, 23, 42, 0.08)'),
          ]
        : undefined,
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: mode === 'dark' ? '#121212' : '#eef2f6',
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: mode === 'dark' ? '#1e1e1e' : '#ffffff',
            color: mode === 'dark' ? '#ffffff' : 'rgba(0, 0, 0, 0.87)',
            boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
            borderBottom: `1px solid ${mode === 'dark' ? '#333' : '#e0e0e0'}`,
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: mode === 'dark' ? '#1e1e1e' : '#ffffff',
            borderRight: `1px solid ${mode === 'dark' ? '#333' : '#e0e0e0'}`,
          },
        },
      },
      MuiPaper: {
        defaultProps: {
          elevation: mode === 'light' ? 1 : 0,
        },
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            ...(mode === 'light'
              ? {
                  border: '1px solid #e2e8f0',
                }
              : {}),
          },
          elevation1: {
            boxShadow: '0 1px 2px rgba(15, 23, 42, 0.05)',
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          indicator: {
            height: 3,
            borderRadius: '3px 3px 0 0',
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 500,
            minHeight: 48,
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: ({ theme: t }) => ({
            borderRadius: 10,
            '&.Mui-selected': {
              backgroundColor:
                t.palette.mode === 'light'
                  ? alpha(t.palette.primary.main, 0.1)
                  : alpha(t.palette.primary.main, 0.18),
              '&:hover': {
                backgroundColor:
                  t.palette.mode === 'light'
                    ? alpha(t.palette.primary.main, 0.14)
                    : alpha(t.palette.primary.main, 0.22),
              },
            },
          }),
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: 8,
          },
          contained: {
            boxShadow: 'none',
            '&:hover': { boxShadow: '0 2px 8px rgba(15, 23, 42, 0.12)' },
          },
          outlined: {
            borderColor: mode === 'light' ? '#cbd5e1' : undefined,
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            fontWeight: 500,
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: ({ theme }) => ({
            backgroundColor:
              theme.palette.mode === 'dark'
                ? alpha(theme.palette.common.white, 0.06)
                : alpha(theme.palette.common.black, 0.03),
          }),
        },
      },
    },
  });
};
